import { useCallback, useState } from 'react';
import { PageWrapper } from '../../../components/general/PageWrapper.tsx';
import {
  Box,
  Card,
  Center,
  Divider,
  FlatList,
  Heading,
  Icons,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  Pressable,
  Textarea,
  TextareaInput,
  useAlert,
  VStack,
} from '@tonic/central-specialties-ui-themed';
import { EntityCardColumn } from '../../../components/general/EntityCardExplorer/EntityCardColumnsView/EntityCardColumn/EntityCardColumn.tsx';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DB } from '@tonic/central-specialties-utils';
import {
  Button,
  ButtonIcon,
  ButtonText,
  HStack,
  ScrollView,
  Spinner,
  Text,
} from '@gluestack-ui/themed';
import { Crew } from '../ManagePage/ManageCrewsModal/ManageCrewsModal.tsx';
import { FormFieldControl } from '../../../components/general/Form/index.ts';
import { BoxWithHeader } from '../../../components/general/BoxWithHeader.tsx';
import { TextFormField } from '../../../components/general/Form/FormField/specific/TextFormField.tsx';
import { extractImageDataFromUrl } from '../../../utils/extractImageDataFromUrl.ts';

type CrewNewsItemProp = {
  id?: string;
  crewId: string;
  category: string;
  title: string;
  body: string;
  images: {
    id: string;
    url: string;
    thumbnailUrl: string;
    caption?: string;
  }[];
  publishDate: string;
};

type CrewNewsOptions = {
  id: string;
  option: 'Job Info' | 'Driver' | 'Road Crew';
  crewName: string;
};

const fmt = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
});

const emptyCrewItem: Crew = { id: '', name: '', isArchived: false };

interface NewsPageProps {}

export const NewsPage = (_props: NewsPageProps) => {
  const [focusedItem, setFocusedItem] = useState<Crew>(emptyCrewItem);

  const { data: crewRequests, isLoading: isCrewsLoading } = useQuery({
    queryKey: ['crewRequests'],
    queryFn: async () =>
      await DB.GET('/news/crews', {
        params: { query: { includeArchived: false } },
      }).then((res) => {
        return res.data.crews;
      }),
  });

  return (
    <PageWrapper>
      <HStack h="$full" w="$full" pl="$4" space="lg">
        <EntityCardColumn
          items={crewRequests}
          pending={isCrewsLoading}
          categoryName="Crews"
          searchByPaths={['name']}
          CardComponent={({ data }: { data: Crew }) => (
            <ItemNumberCard
              number={data.name}
              description={''}
              onPress={() => setFocusedItem(data)}
              minWidth="$48"
              isSelected={focusedItem?.id === data.id}
            />
          )}
          containerProps={{
            maxWidth: '$80',
            minWidth: '$56',
            paddingTop: '$4',
          }}
        />
        <Divider subtle orientation="vertical" />
        <HStack h="$full" flex={focusedItem ? 2 : 1}>
          <Divider subtle orientation="vertical" />
          <VStack h="$full" space="lg" w="$full">
            <ViewNewsItems
              key={focusedItem?.id || 'empty'}
              item={focusedItem.id ? focusedItem : emptyCrewItem}
            />
          </VStack>
        </HStack>
      </HStack>
    </PageWrapper>
  );
};

export interface ItemNumberCardProps {
  number: string | number;
  description: string;
  onPress: () => void;
  isSelected?: boolean;
}

export const ItemNumberCard = ({
  number,
  description,
  onPress,
  isSelected,
}: ItemNumberCardProps) => {
  return (
    <Card
      onPress={onPress}
      hardShadow="1"
      borderWidth={2}
      borderColor={isSelected ? '$primary600' : 'transparent'}
    >
      <VStack gap="$0.5" flex={1}>
        <Text bold>{number}</Text>
        <Text>{description}</Text>
      </VStack>
    </Card>
  );
};

const ViewNewsItems = ({ item }: { item: Crew }) => {
  const { id: crewId, name: crewName } = item;
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newImages, setNewImages] = useState<string[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [editImages, setEditImages] = useState([]);
  const { alert } = useAlert();

  const toggleExpand = (itemId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const categoryMap: Record<CrewNewsOptions['option'], string> = {
    'Job Info': 'JobInfo',
    Driver: 'Driver',
    'Road Crew': 'RoadCrew',
  } as const;
  const options = ['Job Info', 'Driver', 'Road Crew'];
  const [selectedOption, setSelectedOption] = useState('');
  const category = options.includes(selectedOption)
    ? categoryMap[selectedOption]
    : null;

  const {
    data: thread = [],
    isLoading,
    refetch: newsRefetch,
  } = useQuery({
    queryKey: ['crewNewsThread', crewId, selectedOption],
    queryFn: async () => {
      const res = await DB.GET('/news', {
        params: { query: { crewId, category } },
      });

      return res.data.items as CrewNewsItemProp[];
    },
    enabled:
      crewId !== '' && crewId != null && category !== '' && category != null,
  });

  const { mutateAsync: addNewsRequest } = useMutation({
    mutationFn: (data: CrewNewsItemProp) => DB.POST('/news', { body: data }),
  });

  const { mutateAsync: addNewsImageRequest } = useMutation({
    mutationFn: (imageData: string) =>
      DB.POST('/news/image', { body: { imageData } }),
  });

  const { mutateAsync: updateNewsRequest } = useMutation({
    mutationFn: ({
      data,
      newsId,
    }: {
      data: CrewNewsItemProp;
      newsId: string;
    }) =>
      DB.PUT('/news/{id}', { params: { path: { id: newsId } }, body: data }),
  });

  const { mutateAsync: deleteNewsRequest } = useMutation({
    mutationFn: (newsId: string) =>
      DB.DELETE('/news/{id}', { params: { path: { id: newsId } } }),
  });

  const { mutateAsync: deleteNewsImageRequest } = useMutation({
    mutationFn: (imageId: string) =>
      DB.DELETE('/news/image/{id}', { params: { path: { id: imageId } } }),
  });

  const submitNews = useCallback(async () => {
    if (!newText.trim()) {
      return;
    }

    const imageIdPromises = newImages?.map(async (imgUrl) => {
      try {
        const imageUrl = typeof imgUrl === 'string' ? imgUrl : imgUrl?.url;
        const imageData = await extractImageDataFromUrl(imageUrl);
        const { data: newImgData } = await addNewsImageRequest(imageData);
        if (newImgData && newImgData?.id) {
          return newImgData;
        } else {
          console.error(`Failed to upload image ${imgUrl}: Response not OK.`);
          return null;
        }
      } catch (error) {
        console.error(`Error processing image ${imgUrl}:`, error);
        return null;
      }
    });

    const newImageDataArray = await Promise.all(imageIdPromises);
    const successfulUploads = newImageDataArray.filter((data) => data !== null);

    try {
      await addNewsRequest({
        crewId: crewId,
        category,
        title: newTitle,
        body: newText,
        images: successfulUploads,
        publishDate: new Date().toISOString(),
      }).then((res) => {
        setNewText('');
        setNewTitle('');
        setNewImages([]);
        newsRefetch();
        setIsAdding(false);
        alert({
          status: 'success',
          message: 'News item created',
          timeout: 5000,
        });

        return res;
      });
    } catch (error) {
      alert({
        status: 'error',
        message: 'There was an error creating the news item',
        timeout: 5000,
      });
      console.error('There was an error adding news item:', error);
    }
  }, [
    addNewsImageRequest,
    addNewsRequest,
    alert,
    category,
    crewId,
    newImages,
    newText,
    newTitle,
    newsRefetch,
  ]);

  const handleSetOption = (option) => {
    setSelectedOption((prev) => (prev !== option ? option : ''));
  };

  if (crewId == null || crewId === '') {
    return (
      <Center flex={1}>
        <Text size="lg">Select Crew to view</Text>
      </Center>
    );
  }

  if (isLoading) {
    return (
      <Center flex={1}>
        <Spinner />
      </Center>
    );
  }

  return (
    <VStack space="lg" h="$full">
      <Center h="$20" bg="#F8F8F8">
        <Heading color="$black">{crewName}</Heading>
        <HStack space="xl" alignItems="center" justifyContent="space-between">
          <Text size="xl" color="$black">
            {selectedOption ? '' : `Crew Communication Category:  `}
          </Text>
          {options.map((option) => (
            <Button
              key={option}
              onPress={() => handleSetOption(option)}
              borderWidth={'$1'}
              borderColor={selectedOption === option ? '$black' : 'transparent'}
              backgroundColor={
                selectedOption === option ? '$primary700' : '$primary200'
              }
            >
              <ButtonText>{option}</ButtonText>
            </Button>
          ))}
          {selectedOption && (
            <Button onPress={() => setIsAdding(true)} variant="outline">
              <ButtonIcon as={Icons.Plus} color={'$primary700'} size="sm" />
              <ButtonText>Add News Item</ButtonText>
            </Button>
          )}
        </HStack>
      </Center>
      <Box flex={1} w="$full" h="$full" px="$12">
        <ScrollView py="$10" px="$0.5" h="$full">
          <VStack flex={1} px="$4" space="md" justifyContent="flex-start">
            <Text fontSize={'$lg'} fontWeight="$bold">
              {selectedOption ? `${selectedOption} News:` : null}
            </Text>
            {!selectedOption ? (
              <Text size="lg">
                Please select a crew communication category above to see
                assigned news articles.
              </Text>
            ) : thread.length === 0 ? (
              <Text>
                No posts yet for this crew and category -- Be on the lookout for
                news soon!
              </Text>
            ) : (
              <FlatList
                data={thread}
                keyExtractor={(item) => item?.id}
                horizontal={false}
                width={'$full'}
                showsHorizontalScrollIndicator={false}
                scrollEnabled
                renderItem={({ item }: { item: CrewNewsItemProp }) => {
                  const isExpanded = expandedIds.has(item.id);
                  const isEditing = editingId === item.id;

                  // Start editing
                  const onEdit = () => {
                    setEditingId(item.id!);
                    setEditTitle(item.title);
                    setEditText(item.body);
                    setEditImages(item.images);
                  };

                  const onDelete = async () => {
                    const confirmed = window.confirm(
                      'Are you sure you want to delete this item?',
                    );

                    if (confirmed) {
                      await deleteNewsRequest(item?.id ?? '');
                      newsRefetch();
                      alert({
                        status: 'success',
                        message: 'Deleted the news item',
                        timeout: 5000,
                      });
                    }
                  };

                  return (
                    <VStack w="$full" alignItems="center" marginVertical={'$3'}>
                      <Pressable
                        onPress={() => toggleExpand(item.id)}
                        w={'$full'}
                      >
                        <BoxWithHeader
                          key={item?.id}
                          title={item?.title}
                          subtitle={`Published on ${fmt.format(
                            new Date(item.publishDate),
                          )}`}
                          minWidth={'$92'}
                          icon={
                            isExpanded ? Icons.ChevronUp : Icons.ChevronDown
                          }
                        >
                          {isExpanded && !isEditing && (
                            <HStack
                              w="$full"
                              justifyContent="flex-end"
                              space="sm"
                            >
                              <Button onPress={onEdit} variant="ghost">
                                <ButtonIcon
                                  as={Icons.Edit}
                                  color="$primary700"
                                  size="sm"
                                />
                              </Button>
                              <Button onPress={onDelete} variant="ghost">
                                <ButtonIcon
                                  as={Icons.Trash2}
                                  color="$red600"
                                  size="sm"
                                />
                              </Button>
                            </HStack>
                          )}
                          {isEditing ? (
                            <VStack px="$2" py="$1" space="sm" width={'$full'}>
                              <TextFormField
                                multiline={false}
                                placeholder="Title"
                                label="Title"
                                value={editTitle}
                                onChange={setEditTitle}
                              />
                              <Textarea
                                size="md"
                                isDisabled={false}
                                w="$full"
                                h={'$1/2'}
                              >
                                <TextareaInput
                                  value={editText}
                                  onChangeText={setEditText}
                                  placeholder="Body"
                                />
                              </Textarea>
                              <Divider orientation="horizontal" />
                              <FormFieldControl
                                type="Images"
                                label="Remove or Add Images"
                                value={editImages}
                                onChange={(images) => {
                                  const imagesFlat = images
                                    ? images.flat()
                                    : [];
                                  setEditImages(imagesFlat);
                                }}
                                placeholder={'No images attached to this story'}
                                addButtonSize="md"
                                handleImageDelete={deleteNewsImageRequest}
                              />
                              <HStack space="sm" paddingBottom="$6">
                                <Button
                                  onPress={async () => {
                                    const uploadPromises = editImages.map(
                                      async (image) => {
                                        const uri = image?.url ?? '';
                                        const existing = item.images.find(
                                          (img) => img.url === uri,
                                        );
                                        if (existing?.id) {
                                          return existing;
                                        }
                                        const data =
                                          await extractImageDataFromUrl(uri);
                                        const { data: newsImageData } =
                                          await addNewsImageRequest(data);
                                        return newsImageData;
                                      },
                                    );
                                    const finalImages = (
                                      await Promise.all(uploadPromises)
                                    ).filter(Boolean);
                                    await updateNewsRequest({
                                      data: {
                                        ...item,
                                        title: editTitle,
                                        body: editText,
                                        images: finalImages,
                                      },
                                      newsId: item?.id ?? '',
                                    });
                                    setEditingId(null);
                                    newsRefetch();
                                    alert({
                                      status: 'success',
                                      message: 'Updated the news item',
                                      timeout: 5000,
                                    });
                                  }}
                                >
                                  <ButtonText>Save</ButtonText>
                                </Button>
                                <Button
                                  variant="ghost"
                                  onPress={() => setEditingId(null)}
                                >
                                  <ButtonText>Cancel</ButtonText>
                                </Button>
                              </HStack>
                            </VStack>
                          ) : isExpanded ? (
                            <VStack px="$2" py="$1" space="md">
                              <Text justifyContent="flex-start">
                                {item.body}
                              </Text>
                              <Divider orientation="horizontal" />
                              <HStack space="sm">
                                <FormFieldControl
                                  type="Images"
                                  readonly
                                  value={item.images}
                                  displayHorizontal
                                  placeholder="No images attached to this story"
                                />
                              </HStack>
                            </VStack>
                          ) : (
                            <HStack px="$2" py="$1" space="md">
                              <Text
                                justifyContent="flex-start"
                                numberOfLines={4}
                                ellipsizeMode="tail"
                              >
                                {item.body}
                              </Text>
                            </HStack>
                          )}
                        </BoxWithHeader>
                      </Pressable>
                    </VStack>
                  );
                }}
              />
            )}
          </VStack>
          <Modal isOpen={isAdding} onClose={() => setIsAdding(false)}>
            <ModalBackdrop />
            <ModalContent>
              <ModalHeader
                bg="$primary700"
                alignItems="center"
                justifyContent="space-between"
                py="$2"
              >
                <Box w="$8" />
                <Heading size="xl" color="$white">
                  New {selectedOption} Item
                </Heading>
                <ModalCloseButton
                  w="$8"
                  justifyContent="center"
                  alignItems="center"
                >
                  <ButtonIcon color="$white" size="xl" as={Icons.X} />
                </ModalCloseButton>
              </ModalHeader>
              <ModalBody>
                <VStack flex={1} p="$4" space="lg">
                  <TextFormField
                    multiline={false}
                    placeholder="Type your title here…"
                    label="Title"
                    value={newTitle}
                    onChange={(evtOrText) => {
                      // if evtOrText.nativeEvent exists, pull out text; otherwise assume it's the string
                      const text =
                        typeof evtOrText === 'string'
                          ? evtOrText
                          : evtOrText.nativeEvent?.text ?? '';
                      setNewTitle(text);
                    }}
                  />
                  <Textarea
                    size="md"
                    isReadOnly={false}
                    isInvalid={false}
                    isDisabled={false}
                    w="100%"
                    h={80}
                  >
                    <TextareaInput
                      placeholder="Type your news here…"
                      onChange={(evtOrText) => {
                        // if evtOrText.nativeEvent exists, pull out text; otherwise assume it's the string
                        const text =
                          typeof evtOrText === 'string'
                            ? evtOrText
                            : evtOrText.nativeEvent?.text ?? '';
                        setNewText(text);
                      }}
                      style={{ fontSize: 12, backgroundColor: '#FFF' }}
                      defaultValue={''}
                    />
                  </Textarea>
                  <Divider orientation="horizontal" />
                  <HStack width={'$full'}>
                    <FormFieldControl
                      type="Images"
                      label="News Images"
                      value={newImages}
                      onChange={(images) => {
                        const imagesFlat = images ? images.flat() : [];

                        setNewImages(imagesFlat);
                      }}
                      placeholder="Option to add photo to news article"
                      addButtonSize="md"
                      handleImageDelete={deleteNewsImageRequest}
                    />
                  </HStack>
                  <VStack space="sm">
                    <Button
                      onPress={submitNews}
                      disabled={!newText || !newTitle}
                      opacity={!newText || !newTitle ? 0.5 : 1}
                    >
                      <ButtonText>Submit</ButtonText>
                    </Button>
                    <Button variant="ghost" onPress={() => setIsAdding(false)}>
                      <ButtonText>Cancel</ButtonText>
                    </Button>
                  </VStack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>
        </ScrollView>
      </Box>
    </VStack>
  );
};
